// Client mirror of functions-common/src/charts/engine/topN.ts. Keep in sync.

import type { ChartSeries, ChartSeriesPoint } from '../types.js'
import { CHART_KEY_OTHER, CHART_LABEL_KEY_OTHER } from '../limits.js'

export function applyTopNAndOther(
  seriesList: ChartSeries[],
  topN?: number,
  includeOther: boolean = true
): ChartSeries[] {
  if (topN == null || topN <= 0 || seriesList.length <= topN) {
    return seriesList
  }

  // Calculate total y across points for each series to rank them
  const seriesTotals = seriesList.map((series) => {
    const total = series.points.reduce((sum, pt) => sum + pt.y, 0)
    return { series, total }
  })

  // Sort descending by total
  seriesTotals.sort((a, b) => b.total - a.total)

  const topSeries = seriesTotals.slice(0, topN).map((item) => item.series)
  const remainingSeries = seriesTotals.slice(topN).map((item) => item.series)

  if (remainingSeries.length === 0 || !includeOther) {
    return topSeries
  }

  // Merge remaining series into an 'Other' series
  const otherPointsMap = new Map<string | number, number>()
  for (const series of remainingSeries) {
    for (const pt of series.points) {
      const current = otherPointsMap.get(pt.x) ?? 0
      otherPointsMap.set(pt.x, current + pt.y)
    }
  }

  const otherPoints: ChartSeriesPoint[] = Array.from(otherPointsMap.entries()).map(
    ([x, y]) => ({ x, y })
  )

  const axis = remainingSeries[0]?.axis
  const otherSeries: ChartSeries = {
    key: CHART_KEY_OTHER,
    label: 'Other',
    labelKey: CHART_LABEL_KEY_OTHER,
    points: otherPoints,
    ...(axis ? { axis } : {}),
  }

  return [...topSeries, otherSeries]
}
